import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

export const sortMessagesChronologically = messages =>
  [...messages].sort((left, right) => {
    const timestampOrder = (left.created_at || 0) - (right.created_at || 0);
    return timestampOrder || (left.id || 0) - (right.id || 0);
  });

const STATUS_ENUM = {
  0: 'open',
  1: 'resolved',
  2: 'pending',
  3: 'snoozed',
};

const normalizeStatus = value => {
  if (typeof value === 'number') {
    return STATUS_ENUM[value] || value;
  }
  return (value || '').toString();
};

export const filterByStatus = (chatStatus, filterStatus) => {
  if (filterStatus === 'all') {
    return true;
  }
  return normalizeStatus(chatStatus) === normalizeStatus(filterStatus);
};

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const isConversationMine = (
  conversation,
  currentUserId,
  currentUserTeamIds = []
) => {
  const { assignee, team } = conversation.meta || {};
  const isAssignedToUser = Number(assignee?.id) === Number(currentUserId);
  const isAssignedToUserTeam = currentUserTeamIds.some(
    teamId => Number(teamId) === Number(team?.id)
  );

  return isAssignedToUser || isAssignedToUserTeam;
};

export const isConversationUnassigned = (conversation, teamId) => {
  const team = conversation.meta?.team;
  const isOnSelectedTeam = teamId && Number(team?.id) === Number(teamId);

  return (
    !conversation.meta?.assignee &&
    (!team || isOnSelectedTeam) &&
    !conversation.group
  );
};

export const filterGroupsByAssigneeType = (conversations, assigneeType) => {
  if (assigneeType === 'groups') {
    return conversations.filter(conversation => conversation.group);
  }
  if (assigneeType === 'me') {
    return conversations;
  }
  return conversations.filter(conversation => !conversation.group);
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

const filterByInternal = (
  shouldFilter,
  conversationType,
  conversation = {}
) => {
  const inbox = conversation.inbox || {};
  const metaInbox = conversation.meta?.inbox || {};
  const channelType =
    inbox.channel_type ||
    metaInbox.channel_type ||
    conversation.meta?.channel ||
    conversation.channel ||
    conversation.channel_type ||
    conversation.channelType;

  if (!channelType) {
    return shouldFilter;
  }

  const isInternalConversation = channelType === 'Channel::Internal';

  if (conversationType === 'internal') {
    return isInternalConversation && shouldFilter;
  }

  return !isInternalConversation && shouldFilter;
};

const filterByGroups = (shouldFilter, assigneeType, conversation = {}) => {
  if (assigneeType === 'groups') {
    return !!conversation.group && shouldFilter;
  }

  if (assigneeType === 'me') return shouldFilter;

  return !conversation.group && shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const {
    assigneeType,
    inboxId,
    status,
    labels = [],
    teamId,
    conversationType,
  } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;
  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );
  shouldFilter = filterByInternal(shouldFilter, conversationType, conversation);
  shouldFilter = filterByGroups(shouldFilter, assigneeType, conversation);

  return shouldFilter;
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  priority_desc_created_at_asc: ['sortOnPriorityCreatedAt', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnPriorityCreatedAt: (a, b) => {
    const DEFAULT_FOR_NULL = 0;
    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    if (p1 !== p2) return p2 - p1;
    return a.created_at - b.created_at;
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};
